import { authenticatedFetch } from '../utils/authenticatedFetch';
import {
  Inventory,
  InventoryCategory,
  Warehouse,
  Provider,
} from 'public-contracts';
import { configs } from '../configs';

export interface InventoryCreateEntry {
  code: string;
  name: string;
  description: string;
  quantity: number;
  totalPrice: number;
  currency: string;
  warehouseId: string;
  inventoryCategoryId: string;
  providerId?: string;
  ingredientId?: string; // Direct link to ingredient (if ingredient-based)
  productId?: string; // Direct link to product (if product-based)
  width?: number;
  height?: number;
  weight?: number;
  weightUnit?: string; // Weight unit (e.g., 'kg', 'g', 'lb', 'oz')
  volume?: number;
  volumeUnit?: string; // Volume unit (e.g., 'l', 'ml', 'm³', 'cm³')
  isPackage: boolean;
  itemCount?: number; // Number of items inside each package (optional, only relevant when isPackage = true)
  quantityAnchor?: 'ITEM_COUNT' | 'WEIGHT' | 'VOLUME'; // Which factor to use as anchor for quantity calculation
  remainingItems?: number; // Total remaining items (for ITEM_COUNT anchor)
  remainingWeight?: number; // Total remaining weight (for WEIGHT anchor)
  remainingVolume?: number; // Total remaining volume (for VOLUME anchor)
}

export interface InventoryUpdateEntry extends Partial<InventoryCreateEntry> {
  id: string;
}

export class InventoryService {
  private static baseUrl = `${configs.apiRoot}/inventories`;

  // Get all inventories with pagination and filtering
  static async getInventories(params?: {
    page?: number;
    size?: number; // Changed from limit to size to match master-data-api
    search?: string;
    warehouseId?: string;
    categoryId?: string;
  }): Promise<{ data: Inventory[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.size) searchParams.append('size', params.size.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.warehouseId)
      searchParams.append('warehouseId', params.warehouseId);
    if (params?.categoryId)
      searchParams.append('categoryId', params.categoryId);

    const url = `${this.baseUrl}?${searchParams.toString()}`;
    try {
      const response = await authenticatedFetch(url);

      if (!response.ok) {
        console.error(
          `Failed to fetch inventories: ${response.status} ${response.statusText}`
        );
        return { data: [], total: 0 };
      }

      // Extract total count from Content-Range header if available
      // Format: "inventories 0-9/100"
      const contentRange = response.headers.get('Content-Range');
      let total = 0;
      if (contentRange) {
        const match = contentRange.match(/\/(\d+)$/);
        if (match) {
          total = parseInt(match[1]);
        }
      }

      const data = await response.json();
      // Handle both response formats: { success: true, data: [...] } or direct array
      let inventories: Inventory[] = [];
      if (Array.isArray(data)) {
        inventories = data;
      } else {
        inventories = data.data || data || [];
      }

      // If total wasn't in header, use array length (fallback)
      if (total === 0 && inventories.length > 0) {
        total = inventories.length;
      }

      return {
        data: inventories,
        total,
      };
    } catch (err) {
      console.error('Failed to fetch inventories:', err);
      return { data: [], total: 0 };
    }
  }

  // Get single inventory by ID
  static async getInventory(id: string): Promise<Inventory> {
    const response = await authenticatedFetch(`${this.baseUrl}/${id}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch inventory: ${response.statusText}`);
    }

    return response.json();
  }

  // Create new inventory
  static async createInventory(
    inventory: InventoryCreateEntry
  ): Promise<Inventory> {
    const response = await authenticatedFetch(`${this.baseUrl}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(inventory),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to create inventory: ${response.statusText}`
      );
    }

    return response.json();
  }

  // Update inventory
  static async updateInventory(
    id: string,
    inventory: Partial<InventoryCreateEntry>
  ): Promise<Inventory> {
    const response = await authenticatedFetch(`${this.baseUrl}/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(inventory),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to update inventory: ${response.statusText}`
      );
    }

    return response.json();
  }

  // Delete inventory
  static async deleteInventory(id: string): Promise<void> {
    const response = await authenticatedFetch(`${this.baseUrl}/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to delete inventory: ${response.statusText}`
      );
    }
  }

  // Check stock availability
  static async checkStock(id: string, quantity: number): Promise<boolean> {
    const response = await authenticatedFetch(
      `${this.baseUrl}/${id}/check-stock`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity }),
      }
    );

    return response.ok;
  }

  // Get inventory categories
  static async getInventoryCategories(): Promise<InventoryCategory[]> {
    const url = `${configs.apiRoot}/inventory_categories`;
    console.log('🔍 Fetching categories from:', url);
    try {
      const response = await authenticatedFetch(url);
      console.log('🔍 Categories response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Categories API error:', errorText);
        return [];
      }

      const data = await response.json();
      console.log('🔍 Categories data:', data);
      // Handle both response formats: { success: true, data: [...] } or direct array
      if (Array.isArray(data)) {
        return data;
      }
      return data.data || data || [];
    } catch (err) {
      console.error('🔍 Failed to fetch categories:', err);
      return [];
    }
  }

  // Get warehouses
  static async getWarehouses(): Promise<Warehouse[]> {
    const url = `${configs.apiRoot}/warehouses`;
    console.log('🔍 Fetching warehouses from:', url);
    try {
      const response = await authenticatedFetch(url);
      console.log('🔍 Warehouses response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Warehouses API error:', errorText);
        return [];
      }

      const data = await response.json();
      console.log('🔍 Warehouses data:', data);
      // Handle both response formats: { success: true, data: [...] } or direct array
      if (Array.isArray(data)) {
        return data;
      }
      return data.data || data || [];
    } catch (err) {
      console.error('🔍 Failed to fetch warehouses:', err);
      return [];
    }
  }

  // Get providers
  static async getProviders(): Promise<Provider[]> {
    const url = `${configs.apiRoot}/providers`;
    console.log('🔍 Fetching providers from:', url);
    try {
      const response = await authenticatedFetch(url);
      console.log('🔍 Providers response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔍 Providers API error:', errorText);
        return [];
      }

      const data = await response.json();
      console.log('🔍 Providers data:', data);
      // Handle both response formats: { success: true, data: [...] } or direct array
      if (Array.isArray(data)) {
        return data;
      }
      return data.data || data || [];
    } catch (err) {
      console.error('🔍 Failed to fetch providers:', err);
      return [];
    }
  }

  // Generate next inventory code for a category
  static async generateNextCode(categoryId: string): Promise<string> {
    try {
      const response = await authenticatedFetch(
        `${this.baseUrl}/generate-code?categoryId=${categoryId}`
      );

      if (!response.ok) {
        // If endpoint doesn't exist, generate a simple code
        const timestamp = Date.now().toString().slice(-8);
        return `INV-${timestamp}`;
      }

      const data = await response.json();
      return data.code || `INV-${Date.now().toString().slice(-8)}`;
    } catch (error) {
      // Fallback to timestamp-based code if API call fails
      console.warn('Failed to generate code from API, using fallback');
      const timestamp = Date.now().toString().slice(-8);
      return `INV-${timestamp}`;
    }
  }

  // Stock inventory (add stock)
  static async stockInventory(
    id: string,
    quantity: number
  ): Promise<Inventory> {
    const response = await authenticatedFetch(
      `${configs.apiRoot}/inventory/stock`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id, quantity }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message || `Failed to stock inventory: ${response.statusText}`
      );
    }

    return response.json();
  }

  // Update remaining values and recalculate quantity based on anchor
  // Also handles direct quantity updates for non-package items
  static async updateRemainingValues(
    inventoryId: string,
    updates: {
      quantity?: number; // For non-package items
      remainingItems?: number; // For package items
      remainingWeight?: number; // For package items
      remainingVolume?: number; // For package items
    }
  ): Promise<{ newQuantity: number }> {
    const url = `${configs.apiRoot}/inventory/${inventoryId}/update-remaining`;
    console.log('🔍 updateRemainingValues calling:', {
      url,
      method: 'PUT',
      updates,
    });

    const response = await authenticatedFetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.message ||
          `Failed to update remaining values: ${response.statusText}`
      );
    }

    const data = await response.json();
    return { newQuantity: data.newQuantity };
  }
}
